// The guide drawn while a drag is held to one axis: translucent planes
// extending the product's box faces along the axis, and a solid outline where
// they meet the floor or a wall.
import * as THREE from 'three';
import {Axis, axisIndex} from 'components/customer/RoomPlanner/helpers/axes';
import {
    flattenBox,
    overlapOn,
    thinnestAxis,
} from 'components/customer/RoomPlanner/helpers/boxes';

// The guide along a held axis: the product's box faces that run along it,
// extended both ways, solid for 500 mm from the box and faded out by 5 m.
export const AXIS_GUIDE_SOLID_LENGTH = 100;
export const AXIS_GUIDE_FADE_LENGTH = 1000;
export const AXIS_GUIDE_OPACITY = 0.18;

// How far apart a plane and a surface may be and still count as lying on it,
// in scene units (10 mm): products stand a hair off walls and floors.
const ON_SURFACE_TOLERANCE = 2;

/**
 * The face of a wall or floor slab towards the product, as a box flat along
 * the slab's thin axis.
 * @param {THREE.Box3} slab The wall or floor.
 * @param {THREE.Box3} bbox The product's box.
 * @return {object} The face, and the axis it is flat along.
 */
const faceTowards = (
    slab: THREE.Box3,
    bbox: THREE.Box3
): {face: THREE.Box3; normal: number} => {
    const normal = thinnestAxis(slab);
    const centre = bbox.getCenter(new THREE.Vector3()).getComponent(normal);
    const min = slab.min.getComponent(normal);
    const max = slab.max.getComponent(normal);
    const at = Math.abs(centre - min) < Math.abs(centre - max) ? min : max;

    return {face: flattenBox(slab, normal, at), normal};
};

/**
 * Where the guide ends either side of the product along the held axis: at its
 * full length, unless the face of a wall or the floor across its path comes
 * first, where it stops. The ends are coordinates, not distances, so a guide
 * stopped by a face ends exactly on it: lo - (lo - at) need not round back to
 * at, and an end a hair short of the face misses it.
 * @param {THREE.Box3} bbox The product's box.
 * @param {number} along The held axis's index.
 * @param {THREE.Box3[]} surfaces The floor's and the visible walls' boxes.
 * @return {number[]} The low and high ends along the axis.
 */
const guideReach = (
    bbox: THREE.Box3,
    along: number,
    surfaces: THREE.Box3[]
): [number, number] => {
    const lo = bbox.min.getComponent(along);
    const hi = bbox.max.getComponent(along);
    const others = [0, 1, 2].filter((index) => index !== along);
    let low = lo - AXIS_GUIDE_FADE_LENGTH;
    let high = hi + AXIS_GUIDE_FADE_LENGTH;

    surfaces.forEach((slab) => {
        const {face, normal} = faceTowards(slab, bbox);
        if (normal !== along) return;

        // Only a face across the planes' path stops them.
        const across = others.every(
            (index) => overlapOn(face, bbox, index) > 0
        );
        if (!across) return;

        const at = face.min.getComponent(along);
        if (at <= lo) low = Math.max(low, at);
        if (at >= hi) high = Math.min(high, at);
    });

    return [low, high];
};

/**
 * @param {number} distance How far from the product's box along the axis.
 * @return {number} The guide's opacity there: solid near the box, fading to
 * nothing at its full length.
 */
const guideOpacity = (distance: number): number =>
    distance <= AXIS_GUIDE_SOLID_LENGTH
        ? AXIS_GUIDE_OPACITY
        : (AXIS_GUIDE_OPACITY * (AXIS_GUIDE_FADE_LENGTH - distance)) /
          (AXIS_GUIDE_FADE_LENGTH - AXIS_GUIDE_SOLID_LENGTH);

/**
 * The guide along the axis a drag is held to: translucent planes extending
 * the product's box faces that run along the axis, out from the box both
 * ways, so the customer sees the corridor the product can travel. Each
 * side stops at the first wall or floor across its path.
 * @param {THREE.Box3} bbox The product's box where it landed.
 * @param {Axis} axis The held axis.
 * @param {THREE.Box3[]} [surfaces] The floor's and the visible walls' boxes.
 * @return {object} The planes as triangles: each vertex's position, three
 * numbers apiece, and its opacity, fading to nothing at full length.
 */
export const axisGuidePlanes = (
    bbox: THREE.Box3,
    axis: Axis,
    surfaces: THREE.Box3[] = []
): {positions: number[]; alphas: number[]} => {
    const along = axisIndex(axis);
    const [u, v] = [0, 1, 2].filter((index) => index !== along);
    const lo = bbox.min.getComponent(along);
    const hi = bbox.max.getComponent(along);
    const [low, high] = guideReach(bbox, along, surfaces);
    // Each side of the box: where along the axis each band starts and ends,
    // and how opaque the planes are there. A surface cutting a side short
    // cuts its fade short too.
    const bands = (from: number, end: number, way: number) => {
        const reach = Math.abs(end - from);
        if (reach === 0) return [];

        const solid =
            reach > AXIS_GUIDE_SOLID_LENGTH
                ? [[from + way * AXIS_GUIDE_SOLID_LENGTH, AXIS_GUIDE_OPACITY]]
                : [];

        return [
            [from, AXIS_GUIDE_OPACITY],
            ...solid,
            [end, guideOpacity(reach)],
        ];
    };
    const sides = [bands(lo, low, -1), bands(hi, high, 1)].filter(
        (stations) => stations.length > 1
    );
    // The four faces: one of the other two axes fixed at the box's min or
    // max, the remaining one spanning the box.
    const faces = [u, v].flatMap((fixed) => {
        const spans = fixed === u ? v : u;

        return [bbox.min, bbox.max].map((edge) => ({
            fixed,
            at: edge.getComponent(fixed),
            spans,
            from: bbox.min.getComponent(spans),
            to: bbox.max.getComponent(spans),
        }));
    });

    const positions: number[] = [];
    const alphas: number[] = [];
    const vertex = (
        face: (typeof faces)[number],
        [position, alpha]: number[],
        across: number
    ) => {
        const point = new THREE.Vector3();
        point.setComponent(along, position);
        point.setComponent(face.fixed, face.at);
        point.setComponent(face.spans, across);
        positions.push(point.x, point.y, point.z);
        alphas.push(alpha);
    };

    faces.forEach((face) => {
        sides.forEach((stations) => {
            stations.reduce((start, end) => {
                vertex(face, start, face.from);
                vertex(face, end, face.from);
                vertex(face, end, face.to);
                vertex(face, start, face.from);
                vertex(face, end, face.to);
                vertex(face, start, face.to);

                return end;
            });
        });
    });

    return {positions, alphas};
};

/**
 * The two stretches, either side of the product, that the guide planes cover
 * along the held axis, as boxes flat on one other axis: one per plane and
 * side.
 * @param {THREE.Box3} bbox The product's box.
 * @param {number} along The held axis's index.
 * @param {number[]} ends Where the guide ends below and above the box.
 * @return {THREE.Box3[]} The planes.
 */
const guideRects = (
    bbox: THREE.Box3,
    along: number,
    [low, high]: [number, number]
): THREE.Box3[] => {
    const lo = bbox.min.getComponent(along);
    const hi = bbox.max.getComponent(along);
    const others = [0, 1, 2].filter((index) => index !== along);

    return others.flatMap((fixed) =>
        [bbox.min, bbox.max].flatMap((edge) =>
            [
                [low, lo],
                [hi, high],
            ].map(([from, to]) => {
                const rect = flattenBox(bbox, fixed, edge.getComponent(fixed));
                rect.min.setComponent(along, from);
                rect.max.setComponent(along, to);

                return rect;
            })
        )
    );
};

/**
 * Where the guide planes meet the floor or a wall: a segment where a plane
 * crosses a surface, and the two sides running along the held axis where a
 * plane lies on one, as a product slid along the floor or the wall it backs
 * onto does.
 * @param {THREE.Box3} bbox The product's box.
 * @param {Axis} axis The held axis.
 * @param {THREE.Box3[]} surfaces The floor's and the visible walls' boxes.
 * @return {Array<THREE.Vector3[]>} The outline, as pairs of ends, each
 * once.
 */
export const axisGuideOutline = (
    bbox: THREE.Box3,
    axis: Axis,
    surfaces: THREE.Box3[]
): THREE.Vector3[][] => {
    const along = axisIndex(axis);
    const rects = guideRects(bbox, along, guideReach(bbox, along, surfaces));
    // A plane lying on a surface and the planes beside it crossing it meet
    // it along the same lines, so each line is kept once, by its ends.
    const segments = new Map<string, THREE.Vector3[]>();
    const add = (start: THREE.Vector3, end: THREE.Vector3) => {
        const key = [start, end]
            .map((point) => point.toArray().map((n) => n.toFixed(3)))
            .sort()
            .join();
        if (!segments.has(key)) segments.set(key, [start, end]);
    };

    surfaces.forEach((slab) => {
        const {face, normal} = faceTowards(slab, bbox);
        const at = face.min.getComponent(normal);

        rects.forEach((rect) => {
            const lying =
                rect.min.getComponent(normal) === rect.max.getComponent(normal);
            if (
                lying &&
                Math.abs(rect.min.getComponent(normal) - at) >
                    ON_SURFACE_TOLERANCE
            ) {
                return;
            }

            const meet = rect.clone();
            if (lying) {
                meet.min.setComponent(normal, at);
                meet.max.setComponent(normal, at);
            }
            meet.intersect(face);
            if (meet.isEmpty()) return;

            const size = meet.getSize(new THREE.Vector3());
            const flat = [0, 1, 2].filter(
                (index) => size.getComponent(index) === 0
            );

            if (flat.length === 2) {
                // A plane crossing the surface meets it along a line.
                add(meet.min.clone(), meet.max.clone());
            } else if (flat.length === 1 && size.getComponent(along) > 0) {
                // A plane lying on the surface: outline its sides along the
                // axis.
                const across = [0, 1, 2].find(
                    (index) => index !== along && index !== flat[0]
                );
                [meet.min, meet.max].forEach((edge) => {
                    const start = meet.min.clone();
                    const end = meet.max.clone();
                    start.setComponent(across, edge.getComponent(across));
                    end.setComponent(across, edge.getComponent(across));
                    add(start, end);
                });
            }
        });
    });

    return Array.from(segments.values());
};
